/**
 * Utility functions for energy unit conversion and formatting
 */

/**
 * Formats energy values dynamically between kWh and MWh
 * @param {number|string} value - The energy value in kWh
 * @param {number} threshold - Threshold above which to convert to MWh (default: 1000)
 * @param {number} decimalPlaces - Number of decimal places for MWh conversion (default: 2)
 * @returns {string} Formatted string with appropriate unit (e.g., "2.73 MWh" or "850 kWh")
 */
export const formatEnergyValue = (value, threshold = 1000, decimalPlaces = 2) => {
  // Handle null, undefined, or invalid values
  if (value === null || value === undefined || value === '' || isNaN(value)) {
    return 'N/A';
  }

  // Convert to number if it's a string
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Handle invalid numeric values
  if (isNaN(numericValue)) {
    return 'N/A';
  }

  // If value is above threshold, convert to MWh
  if (numericValue >= threshold) {
    const mwhValue = numericValue / 1000;
    return `${mwhValue.toFixed(decimalPlaces)} MWh`;
  }

  // Otherwise, display in kWh with appropriate decimal places
  // For values less than 10, show 2 decimal places; otherwise show 1 or 0
  let kwhDecimalPlaces = 0;
  if (numericValue < 10) {
    kwhDecimalPlaces = 2;
  } else if (numericValue < 100) {
    kwhDecimalPlaces = 1;
  }

  return `${numericValue.toFixed(kwhDecimalPlaces)} kWh`;
};

/**
 * Formats energy values specifically for maximum consumption/production displays
 * This handles the case where the value might already be formatted or needs special handling
 * @param {number|string} value - The energy value in kWh
 * @param {number} threshold - Threshold above which to convert to MWh (default: 1000)
 * @param {number} decimalPlaces - Number of decimal places for MWh conversion (default: 2)
 * @returns {string} Formatted string with appropriate unit
 */
export const formatMaxEnergyValue = (value, threshold = 1000, decimalPlaces = 2) => {
  // Handle null, undefined, or invalid values
  if (value === null || value === undefined || value === '' || isNaN(value)) {
    return 'N/A';
  }

  // Convert to number if it's a string, and handle cases where it might already have .toFixed() applied
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Handle invalid numeric values
  if (isNaN(numericValue)) {
    return 'N/A';
  }

  return formatEnergyValue(numericValue, threshold, decimalPlaces);
}; 